import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/shared/CookieConsent";

// Providers
import { CartProvider } from "@/store/cartContext";
import { AuthProvider } from "@/store/authContext";

// Layouts
import { PublicLayout } from "@/components/layout/PublicLayout";
import { AdminLayout } from "@/components/layout/AdminLayout";

// Public pages
import HomePage from "@/pages/public/HomePage";
import ProductsPage from "@/pages/public/ProductsPage";
import ProductDetailPage from "@/pages/public/ProductDetailPage";
import CartPage from "@/pages/public/CartPage";
import CheckoutPage from "@/pages/public/CheckoutPage";
import OrderSuccessPage from "@/pages/public/OrderSuccessPage";
import ContactPage from "@/pages/public/ContactPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";

// Admin pages
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminOrdersPage from "@/pages/admin/AdminOrdersPage";
import AdminOrderDetailPage from "@/pages/admin/AdminOrderDetailPage";
import AdminCategoriesPage from "@/pages/admin/AdminCategoriesPage";
import AdminProductsPage from "@/pages/admin/AdminProductsPage";

import { SITE_URL, DEFAULT_IMAGE, upsertMeta, upsertCanonical } from "@/utils/seo";

function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const canonicalUrl = `${SITE_URL}${path === "/" ? "/" : path}`;

    const seoByPath = {
      "/": {
        title: "Sjenički proizvodi Prijepolje | Domaći proizvodi — Fazlić",
        description:
          "Sjenički i domaći proizvodi u Prijepolju: sir, kajmak, suvo meso, sudžuk. Online poručivanje sjeničkih proizvoda i dostava. Porodična radnja Fazlić.",
      },
      "/proizvodi": {
        title: "Sjenički proizvodi i domaće namirnice | Ponuda — Fazlić Prijepolje",
        description:
          "Kompletan izbor sjeničkih i domaćih proizvoda u Prijepolju: sir, kajmak, suhomesnato, sudžuk i drugo. Pogledajte aktuelnu ponudu.",
      },
      "/kontakt": {
        title: "Kontakt Prijepolje | Sjenički proizvodi Fazlić",
        description:
          "Kontakt za porudžbine sjeničkih i domaćih proizvoda u Prijepolju. Telefon +381601140402, adresa Izeta Čavića. Lično preuzimanje i dostava.",
      },
      "/politika-privatnosti": {
        title: "Politika privatnosti i kolačića | Sjenički Proizvodi Fazlić",
        description:
          "Kako Sjenički Proizvodi Fazlić koristi kolačiće i obrađuje lične podatke. Podešavanja pristanka na kolačiće.",
      },
      "/korpa": {
        title: "Korpa | Sjenički Proizvodi Fazlić",
        description: "Pregled izabranih proizvoda pre naručivanja.",
        noindex: true,
      },
      "/checkout": {
        title: "Plaćanje i dostava | Sjenički Proizvodi Fazlić",
        description: "Završni korak narudžbine.",
        noindex: true,
      },
      "/uspesna-porudzbina": {
        title: "Uspešna porudžbina | Sjenički Proizvodi Fazlić",
        description: "Potvrda uspešne narudžbine.",
        noindex: true,
      },
    };

    const dynamicProductSeo = path.startsWith("/proizvodi/")
      ? {
          title: "Detalji proizvoda | Sjenički Proizvodi Fazlić",
          description: "Detaljne informacije o odabranom proizvodu.",
        }
      : null;

    const dynamicAdminSeo = path.startsWith("/admin")
      ? {
          title: "Admin | Sjenički Proizvodi Fazlić",
          description: "Administrativna stranica.",
          noindex: true,
        }
      : null;

    const seo = seoByPath[path] || dynamicProductSeo || dynamicAdminSeo || seoByPath["/"];
    const robotsValue = seo.noindex
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "robots", robotsValue);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);
    upsertMeta("property", "og:image:width", "1280");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:image:alt", "Sjenički Proizvodi Fazlić logo");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);
    upsertCanonical(canonicalUrl);
  }, [location.pathname]);

  return null;
}

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    const id = decodeURIComponent(location.hash.replace("#", ""));

    const scrollToElement = () => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timeoutId = window.setTimeout(scrollToElement, 50);
    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.hash]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <RouteSeo />
          <ScrollToHash />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/proizvodi" element={<PublicLayout><ProductsPage /></PublicLayout>} />
            <Route path="/proizvodi/:id" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
            <Route path="/korpa" element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
            <Route path="/uspesna-porudzbina" element={<PublicLayout><OrderSuccessPage /></PublicLayout>} />
            <Route path="/kontakt" element={<PublicLayout><ContactPage /></PublicLayout>} />
            <Route path="/politika-privatnosti" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboardPage /></AdminLayout>} />
            <Route path="/admin/porudžbine" element={<AdminLayout><AdminOrdersPage /></AdminLayout>} />
            <Route path="/admin/porudžbine/:id" element={<AdminLayout><AdminOrderDetailPage /></AdminLayout>} />
            <Route path="/admin/kategorije" element={<AdminLayout><AdminCategoriesPage /></AdminLayout>} />
            <Route path="/admin/proizvodi" element={<AdminLayout><AdminProductsPage /></AdminLayout>} />

            {/* Redirects */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
          <CookieConsent />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
